import { STATUSPAGE } from '@repo/ui';
import {
  GetAllCurricularUnitsUserResponse,
  GetAllGroupsUserResponse,
  GetAllProfessionalsSelectOptionsResponse,
  GetAllSemestersUserResponse,
  GetProfessionalsDailyWorkloadResponse,
} from 'domain/models';
import {
  ExportProfessionalsDailyWorkloadExcelUseCaseInterface,
  GetAllCurricularUnitsUserUseCaseInterface,
  GetAllGroupsUserUseCaseInterface,
  GetAllProfessionalsSelectOptionsUseCaseInterface,
  GetProfessionalsDailyWorkloadUseCaseInterface,
} from 'domain/usecases';
import { notifyError } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useCallback, useState } from 'react';

const EXCEL_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const useProfessionalDailyWorkloadReport = () => {
  const {
    semesterService,
    curricularUnitService,
    groupService,
    professionalService,
    reportService,
  } = useService();

  const [statusGet, setStatusGet] = useState<STATUSPAGE>(STATUSPAGE.DONE);
  const [statusExport, setStatusExport] = useState<STATUSPAGE>(STATUSPAGE.DONE);

  const [semesters, setSemesters] = useState<GetAllSemestersUserResponse[]>([]);
  const [curricularUnits, setCurricularUnits] = useState<
    GetAllCurricularUnitsUserResponse[]
  >([]);
  const [groups, setGroups] = useState<GetAllGroupsUserResponse[]>([]);
  const [professionals, setProfessionals] = useState<
    GetAllProfessionalsSelectOptionsResponse[]
  >([]);
  const [screenData, setScreenData] =
    useState<GetProfessionalsDailyWorkloadResponse | null>(null);

  const requestGetAllSemestersUser = useCallback(async () => {
    return semesterService
      .getAllSemestersUser({ entityId: '' })
      .then((res) => setSemesters(res))
      .catch((err) => {
        notifyError(err.message || 'Erro ao carregar semestres.');
      });
  }, [semesterService]);

  const requestGetAllCurricularUnitsUser = useCallback(
    async (params: GetAllCurricularUnitsUserUseCaseInterface.Params) => {
      return curricularUnitService
        .getAllCurricularUnitsUser(params)
        .then((res) => setCurricularUnits(res))
        .catch((err) => {
          notifyError(err.message || 'Erro ao carregar unidades curriculares.');
        });
    },
    [curricularUnitService],
  );

  const requestGetAllGroupsUser = useCallback(
    async (params: GetAllGroupsUserUseCaseInterface.Params) => {
      return groupService
        .getAllGroupsUser(params)
        .then((res) => setGroups(res))
        .catch((err) => {
          notifyError(err.message || 'Erro ao carregar grupos.');
        });
    },
    [groupService],
  );

  const requestGetAllProfessionalsSelectOptions = useCallback(
    async (params: GetAllProfessionalsSelectOptionsUseCaseInterface.Params) => {
      return professionalService
        .getAllProfessionalsSelectOptions(params)
        .then((res) => setProfessionals(res))
        .catch((err) => {
          notifyError(err.message || 'Erro ao carregar profissionais.');
        });
    },
    [professionalService],
  );

  const requestDailyWorkloadScreen = useCallback(
    async (params: GetProfessionalsDailyWorkloadUseCaseInterface.Params) => {
      setStatusGet(STATUSPAGE.LOADING);

      return reportService
        .getProfessionalsDailyWorkload(params)
        .then((res) => setScreenData(res))
        .catch((err) => {
          setScreenData(null);
          notifyError(
            err.message || 'Ocorreu um erro ao carregar o relatório.',
          );
        })
        .finally(() => {
          setStatusGet(STATUSPAGE.DONE);
        });
    },
    [reportService],
  );

  const exportDailyWorkloadExcel = useCallback(
    async (
      params: ExportProfessionalsDailyWorkloadExcelUseCaseInterface.Params,
    ) => {
      setStatusExport(STATUSPAGE.LOADING);

      return reportService
        .exportProfessionalsDailyWorkloadExcel(params)
        .then((res) => {
          const blob = new Blob([res as unknown as ArrayBuffer], {
            type: EXCEL_MIME_TYPE,
          });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          window.open(link.href, '_blank');
        })
        .catch((err) => {
          notifyError(
            err.message || 'Ocorreu um erro ao exportar o relatório.',
          );
        })
        .finally(() => {
          setStatusExport(STATUSPAGE.DONE);
        });
    },
    [reportService],
  );

  return {
    statusGet,
    statusExport,
    semesters,
    curricularUnits,
    groups,
    professionals,
    screenData,
    requestGetAllSemestersUser,
    requestGetAllCurricularUnitsUser,
    requestGetAllGroupsUser,
    requestGetAllProfessionalsSelectOptions,
    requestDailyWorkloadScreen,
    exportDailyWorkloadExcel,
  };
};
